import { notNull } from "@msys/common";
import {
  DebouncedSearchInput,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  MenuButton,
  MenuItemWithIcon,
  ModalOpenButton,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import {
  Checklist as ChecklistIcon,
  Settings as SettingsIcon,
  ViewKanban as ViewKanbanIcon,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  namedOperations,
  OrganisationProjectsSorting,
  ProjectStateMachineStatus,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../constants.js";
import { ImportCustomGaebP84Button } from "../../features/importExport/gaebImport/ImportCustomGaebP84Button.js";
import { ImportXiopdButton } from "../../features/importExport/xiopdImport/ImportXiopdButton.js";
import { OpportunityListItemDisplayConfig } from "../../features/opportunities/components/OpportunityListItem.js";
import { CreateOpportunityButton } from "../../features/opportunities/CreateOpportunityButton.js";
import { OpportunitiesKanban } from "../../features/opportunities/OpportunitiesKanban.js";
import {
  DEFAULT_SORTING,
  Filters,
  OpportunitiesList,
} from "../../features/opportunities/OpportunitiesList.js";
import {
  defaultListItemDisplayConfig,
  OpportunitiesListItemVisibilitySettingsModal,
} from "../../features/opportunities/OpportunitiesListItemVisibilitySettingsModal.js";
import { ImportVisibilityKeys } from "../../features/organisations/boxes/OrganisationProjectImportSettingsBox.js";
import { FilterProjectAssigneesButton } from "../../features/projects/filters/ProjectAssignees.js";
import { FilterProjectBudgetButton } from "../../features/projects/filters/ProjectBudget.js";
import { ProjectsFilterModal } from "../../features/projects/ProjectsFilterModal.js";
import { getProjectFilterChipsFactory } from "../../features/projects/renderProjectsFilterChips.js";
import { PROJECT_FILTER_MAP } from "../../features/projects/useProjectSources.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { ImportS4HanaSalesQuotationButton } from "../../features/sap-s4-hana/ImportS4HanaSalesQuotationButton.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useListViewCustomQuickFilters } from "../../features/users/useListViewCustomQuickFilters.js";
import { useUserPreference } from "../../features/users/useUserPreference.js";
import { useVisibilityStore } from "../../features/users/useVisibilityStore.js";

const ALLOWED_VIEWS: CollectionView[] = ["table", "list", "kanban"];

interface Props {
  submenuItems: PageTopbarItem[];
}

export function OpportunitiesCurrent({ submenuItems }: Props) {
  const { t } = useTranslate([
    "Projects",
    "Opportunities",
    "Categories",
    "Tickets",
    "Global",
  ]);
  const { isMinTablet } = useScreenWidth();

  const { getFormattedFloat } = useFormatting();

  const viewer = useUserData().currentUser!;
  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const [isConfiguring, setIsConfiguring] = React.useState<boolean>(false);

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "opportunities-current",
      "kanban",
      "list"
    );

  const allowedStates: ProjectStateMachineStatus[] = React.useMemo(
    () => ["opportunity"],
    []
  );

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationProjectsSorting, Filters>(
    {},
    DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const listViewName = "OpportunitiesCurrent";

  const getProjectFilterChips = getProjectFilterChipsFactory({
    viewer,
    t,
    allowedStates,
    getFormattedFloat,
    showPhasesChip: activeView !== "kanban",
  });

  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters<Filters>(
      listViewName,
      filters,
      setFilters,
      [
        {
          filterName: t("Relevant to me", { ns: "Opportunities" }),
          filterValue: {
            filterRelevantToUserIds: [viewer.id],
          },
        },
      ],
      getProjectFilterChips
    );

  const baseVariables = React.useMemo(
    () => ({
      offset,
      limit,
      sorting: DEFAULT_SORTING,
    }),
    [offset, limit]
  );

  const variables = React.useMemo(
    () => ({
      ...baseVariables,
      ...filters,
      filterBySources: filters.sources
        ? filters.sources.map(s => PROJECT_FILTER_MAP[s]).flat(1)
        : undefined,
      filterIncludeState: filters.filterIncludeState?.length
        ? filters.filterIncludeState
        : allowedStates,
      sorting,
    }),
    [baseVariables, filters, sorting, allowedStates]
  );

  const dataGridStateStore = useDataGridStateStore(listViewName);
  const listItemDisplayConfigStore =
    useUserPreference<OpportunityListItemDisplayConfig>(
      `DisplayConfig-${listViewName}`,
      defaultListItemDisplayConfig
    );
  const importVisibilityStore = useVisibilityStore<ImportVisibilityKeys>(
    `${listViewName}-Import`
  );

  const menuItems = !importVisibilityStore.loading
    ? [
        <RestrictedByOrganisationPermissionWithDebug
          permission="CREATE_PROJECT"
          key="import-gaeb"
        >
          {importVisibilityStore.value.gaeb !== false ? (
            <ImportCustomGaebP84Button
              projectParams={{ isTentative: true }}
              refetchQueries={[
                namedOperations.Query.OpportunitiesList,
                namedOperations.Query.OpportunitiesKanbanList,
              ]}
            />
          ) : null}
        </RestrictedByOrganisationPermissionWithDebug>,
        <RestrictedByOrganisationPermissionWithDebug
          permission="CREATE_PROJECT"
          key="import-xiopd"
        >
          {importVisibilityStore.value.xiopd !== false ? (
            <ImportXiopdButton
              projectParams={{ isTentative: true }}
              refetchQueries={[
                namedOperations.Query.OpportunitiesList,
                namedOperations.Query.OpportunitiesKanbanList,
              ]}
            />
          ) : null}
        </RestrictedByOrganisationPermissionWithDebug>,
        hasSapS4HanaIntegration ? (
          <RestrictedByOrganisationPermissionWithDebug
            permission="CREATE_PROJECT"
            key="import-s4-hana-salesquoation"
          >
            <ImportS4HanaSalesQuotationButton
              projectId={null}
              refetchQueries={[
                namedOperations.Query.OpportunitiesList,
                namedOperations.Query.OpportunitiesKanbanList,
              ]}
            />
          </RestrictedByOrganisationPermissionWithDebug>
        ) : null,
      ].filter(notNull)
    : [];

  const settingsItems = [
    activeView !== "table" ? (
      <ModalOpenButton
        key="opportunities-list-item-visibility-settings"
        Modal={OpportunitiesListItemVisibilitySettingsModal}
        modalProps={{
          listItemDisplayConfigStore,
        }}
      >
        <MenuItemWithIcon icon={<ChecklistIcon />}>
          {t("Opportunities view settings", { ns: "Opportunities" })}
        </MenuItemWithIcon>
      </ModalOpenButton>
    ) : null,
    activeView === "kanban" ? (
      <RestrictedByOrganisationPermissionWithDebug
        key="opportunities-configure-pipeline"
        permission="MANAGE_ORG"
      >
        <MenuItemWithIcon
          disabled={isConfiguring}
          icon={<ViewKanbanIcon />}
          onClick={() => {
            setIsConfiguring(true);
          }}
        >
          {t("Configure pipeline", { ns: "Opportunities" })}
        </MenuItemWithIcon>
      </RestrictedByOrganisationPermissionWithDebug>
    ) : null,
  ].filter(notNull);

  const activeFilterChips = getProjectFilterChips({
    filters,
    setFilters,
  });

  return (
    <Page
      subtitle={t("Opportunities", { ns: "Opportunities" })}
      title={t("Current", { ns: "Opportunities" })}
      submenuItems={submenuItems}
    >
      <PageContainer $fullHeight={activeView === "kanban"}>
        <ListHeader
          QuickFilter={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexShrink={1}
              flexGrow={0}
            >
              {isMinTablet ? (
                <>
                  <FilterProjectBudgetButton
                    filters={filters}
                    setFilters={setFilters}
                    variables={variables}
                  />
                  <FilterProjectAssigneesButton
                    unassigned={filters.filterUnassigned ?? false}
                    setUnassigned={unassigned =>
                      setFilters(filters => ({
                        ...filters,
                        filterUnassigned: unassigned || undefined,
                      }))
                    }
                    value={filters.filterAssigneeIds ?? []}
                    setValue={(newValue: string[]) =>
                      setFilters(filters => ({
                        ...filters,
                        filterAssigneeIds: newValue,
                      }))
                    }
                  />
                </>
              ) : undefined}
              {customQuickFilterSelect}
            </Stack>
          }
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS.filter(view => {
                if (
                  viewer.organisation.isClientOrganisation &&
                  view === "kanban"
                )
                  return false;
                return true;
              })}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          FilterButton={
            <ModalOpenButton
              Modal={ProjectsFilterModal}
              modalProps={{
                sorting,
                setSorting,
                filters,
                setFilters,
                resetFilters,
                allowedStates,
                showPhasesFilter: activeView !== "kanban",
                showSorting: activeView !== "kanban",
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          }
          ActionButtons={
            settingsItems.length > 0 ? (
              <MenuButton Icon={<SettingsIcon />}>{settingsItems}</MenuButton>
            ) : undefined
          }
          MenuButton={
            menuItems.length > 0 ? (
              <MenuButton>{menuItems}</MenuButton>
            ) : undefined
          }
          CreateButton={
            <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
              <CreateOpportunityButton pageName="Opportunities" type="button" />
            </RestrictedByOrganisationPermissionWithDebug>
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", {
                ns: "Global",
              })}
              defaultValue={filters.filterSearchTerm}
              onChangeSearchTerm={newValue =>
                setFilters(filters => ({
                  ...filters,
                  filterSearchTerm: newValue,
                }))
              }
            />
          }
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    marginLeft={1}
                    fontWeight="bold"
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
              saveFiltersButton={addCustomFilterButton}
            />
          }
          mb={2}
        />
        {!listItemDisplayConfigStore.loading &&
          (activeView === "kanban" ? (
            <OpportunitiesKanban
              variables={variables}
              displayConfig={listItemDisplayConfigStore.value}
              isConfiguring={isConfiguring}
              setIsConfiguring={setIsConfiguring}
            />
          ) : (
            <OpportunitiesList
              sorting={sorting}
              setSorting={setSorting}
              activeView={activeView}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              showStatus={allowedStates.length > 1}
              showPhase={true}
              stateStore={dataGridStateStore}
              displayConfig={listItemDisplayConfigStore.value}
              variables={variables}
            />
          ))}
      </PageContainer>
    </Page>
  );
}
